import { computed, action, observable } from 'mobx';
import { events, emit, on } from '@estee/elc-service-bus';
import { root } from '@estee/elc-universal-utils';
import { buildIngredientsContent } from '../utils/ContentFormatter';
import { IAsset } from '../domain/entities/Asset';
import { IProductFull, IProductFullConfig } from '../../internal/interfaces/IProductFull';
import {
    ProductInfoPopupFieldType,
    IProductInfoPopupPayload
} from '../constants/ProductInfoPopupFieldType';
import { truncateStringToNearestWord } from '../utils/truncateStringToNearestWord';
import { STICKY_CLASS_NAMES } from '../constants/StickyAddToBag';
import { mergeConfigs } from '../utils/ConfigUtils';
import { IProductControllerData, ProductBaseController } from './ProductBaseController';
import { ProductRepository } from '../repositories/ProductRepository';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '~setup/ProductService';
import { ProductFullService } from '../domain/services/ProductFullService';
import { IFreeShippingAndReturnConfig } from '../interfaces/ITranslations';
import { ComponentTypes } from '../constants/ComponentTypes';
import { Product } from '~domain/entities/Product';
import { ICoreProduct } from '~api/interfaces/IProduct';

interface IProductDetails {
    allIngredients: string;
    greatToKnow: string;
    howDoesItWork: string;
    howToUse: string;
    keyIngredient: string;
    whatItDoes: string;
    whatItIs: string;
    whoItsFor: string;
    fragranceKeyNotes: string;
}

export interface IProductFullViewControllerData extends IProductControllerData {
    howDoesItWorkLabel: string;
    isPerfectShadeEnabled: boolean;
    modelAndSmooshImages: IAsset[];
    productInfoPopupFieldType: string;
    productTruncatedDescription: string;
    showStickyAddToBag: boolean;
    stickyAddToBagVisibleClassName: string;
    isProductInfoPopupOpen?: boolean;
    shouldRenderReadMoreLink?: boolean;
    charLimit?: number;
    enableVtoMakeup?: boolean;
    engravingEnabled?: boolean;
    favoritesEnabled?: boolean;
    isVtoFoundationEnabled?: boolean;
    isVtoMakeupEnabled?: boolean;
    productInfoPopupData?: string;
    productInfoPopupHeader?: string;
    freeShippingAndReturnConfig?: IFreeShippingAndReturnConfig;
    isLeftShortDescriptionEnabled?: boolean;
    isRightShortDescriptionEnabled?: boolean;
    isVtoFoundationDesktopEnabled?: boolean;
}

export class ProductFullViewController extends ProductBaseController {
    @observable public isLoaded: boolean = false;
    @observable public addingToBag: boolean = false;
    // if the add to bag fails in the first instance, will persist the disabled state of the button
    @observable public disableAddToCart: boolean = false;
    @observable public showStickyAddToBag: boolean = false;
    @observable public isShowingSuccessLabel: boolean = false;
    @observable public isPerfectShadeEnabled: boolean = false;
    @observable public isProductInfoPopupOpen: boolean = false;
    @observable public isVtoOpened: boolean = false;
    @observable public productInfoPopupFieldType: string;
    @observable public productInfoPopupHeader?: string;
    @observable public currentTabIndex: string = '0';
    @observable public hasShadeSidebarVto: boolean = false;
    @observable private config: IProductFullConfig;
    @observable public productFullService: ProductFullService;
    @observable protected productRepository: ProductRepository;
    @observable protected urlRouteParam: string = '';
    @observable protected urlRouteValue: string = '';
    @observable protected productData?: ICoreProduct[];

    public dataState: IProductFullViewControllerData;
    public configMergeStrategy = mergeConfigs;

    constructor(productFullService: ProductFullService) {
        super();

        this.productFullService = productFullService;
        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @action
    public initialize = (props: IProductFull) => {
        this.config = props.config;
        this.productData = props.productData;
        const { imageCarouselSettings } = this.config;

        const carouselSettings =
            imageCarouselSettings && imageCarouselSettings[ComponentTypes.PRODUCT_FULL];

        this.setMediaSettings(carouselSettings);
    };

    @action
    public preLoaded = () => {
        if (this.productData?.length) {
            this.product = this.productRepository.convertToProductEntity(this.productData)[0];
            this.isLoaded = true;
        }
    };

    @action
    public loaded = async () => {
        const { productId } = this.config;

        const productFullDataParams = await this.productFullService.getProductFullDataParams(
            this.config
        );
        this.product = (await this.productFullService.getProductData(
            { ...productFullDataParams.options, updateInventories: false },
            productFullDataParams.queryFragments
        )) as Product;

        if (this.product) {
            this.productRepository.updateInventoryStatus(this.product);
            this.checkUrlParam();
            this.selectBestShade(true);
            this.isLoaded = true;
            emit(events.PRODUCT_LOADED, [productId]);
            emit(events.PRODUCT_FULL_VIEWED, {
                product: this.product,
                selectedSku: this.selectedSku
            });
            emit(events.PRODUCT_FULL_LOADED, { productId, skuId: this.selectedSku.skuId });
        }
    };

    @computed
    get data(): IProductFullViewControllerData {
        return {
            ...this.baseData,
            howDoesItWorkLabel: this.product.howDoesItWork.label,
            selectedAutoReplenishOption: this.selectedAutoReplenishOption,
            modelAndSmooshImages: this.modelAndSmooshImages,
            isVtoMakeupEnabled: this.vtoMakeupEnabled && this.vtoMakeupFeatureEnabled,
            isVtoFoundationEnabled: this.vtoFoundationEnabled && this.vtoFoundationFeatureEnabled,
            isVtoFoundationDesktopEnabled: this.vtoFoundationDesktopEnabled,
            isPerfectShadeEnabled: this.isPerfectShadeEnabled,
            isSmartGiftEnabled: this.smartGiftEnabled,
            engravingEnabled: this.engravingEnabled,
            favoritesEnabled: this.favoritesEnabled,
            isLoaded: this.isLoaded,
            stickyAddToBagVisibleClassName: this.stickyAddToBagVisibleClassName,
            showStickyAddToBag: this.showStickyAddToBag,
            charLimit: this.charLimit,
            productTruncatedDescription: this.productTruncatedDescription,
            productInfoPopupFieldType: this.productInfoPopupFieldType,
            productInfoPopupHeader: this.productInfoPopupHeader,
            productInfoPopupData: this.productInfoPopupData,
            currentTabId: this.currentTabId,
            hasShadeSidebarVto: this.shadeSidebarVtoEnabled,
            isProductInfoPopupOpen: this.isProductInfoPopupOpen,
            shouldRenderReadMoreLink: this.shouldRenderReadMoreLink,
            ...this.productDetails
        };
    }

    public emitSocialShareIconTriggered = (icon: string) => {
        emit(events.SOCIAL_SHARE_ICON_TRIGGERED, { eventLabel: icon });
    };

    public decodeUrl = (urlParam: string) => {
        return urlParam.split('_').join(' ');
    };

    public checkUrlParam = () => {
        const search = (<Location>root.location).search;
        if (!search) {
            return;
        }
        const urlParams = new URLSearchParams(search);
        const { hasMultipleShades, defaultSku } = this.product;
        const { shades, sizes } = defaultSku || {};
        if (hasMultipleShades) {
            const shadeName = urlParams.get(ProductFullViewController.shadeUrlParam);
            if (!shadeName && shades[0] && shades[0].name) {
                this.urlUpdateState(ProductFullViewController.shadeUrlParam, shades[0].name);
            } else if (shadeName) {
                const decodedShadeName = this.decodeUrl(shadeName);
                this.handleShadeSelect(decodedShadeName);
            }
        } else {
            const sizeLabel = urlParams.get(ProductFullViewController.sizeUrlParam);
            if (!sizeLabel && sizes[0] && sizes[0].value) {
                this.urlUpdateState(ProductFullViewController.sizeUrlParam, sizes[0].value);
            } else if (sizeLabel) {
                const decodedSizeLabel = this.decodeUrl(sizeLabel);
                this.handleSizeSelect(decodedSizeLabel);
            }
        }
    };

    @computed
    public get modelAndSmooshImages() {
        let modelAndSmooshImages: IAsset[] = [];
        const { selectedSku } = this;

        if (selectedSku) {
            if (selectedSku && selectedSku.modelImage && selectedSku.modelImage.src) {
                modelAndSmooshImages = [selectedSku.modelImage];
            }
            if (selectedSku && selectedSku.smooshImage && selectedSku.smooshImage.src) {
                modelAndSmooshImages = [...modelAndSmooshImages, selectedSku.smooshImage];
            }
        }

        return modelAndSmooshImages;
    }

    @computed
    public get vtoFoundationEnabled() {
        const features = this.config && this.config.features;

        return features && features.vtoFoundation && features.vtoFoundation.enabled;
    }

    @computed
    public get vtoMakeupEnabled() {
        const features = this.config && this.config.features;

        return features && features.vtoMakeup && features.vtoMakeup.enabled;
    }

    @computed
    public get vtoMakeupFeatureEnabled() {
        const vto = this.vtoFeature;
        const hasColorExperience = this.product.hasColorExperience;

        return vto && vto.makeup && vto.makeup.enabled && hasColorExperience;
    }

    @computed
    public get vtoFoundationFeatureEnabled() {
        const vto = this.vtoFeature;
        const hasFoundationExperience = this.product.hasFoundationExperience;

        return vto && vto.foundation && vto.foundation.enabled && hasFoundationExperience;
    }

    @computed
    public get vtoFoundationDesktopEnabled() {
        const vtoFeature = this.vtoFeature;
        const vtoFoundation = vtoFeature?.foundation;

        return !!(
            this.vtoFoundationFeatureEnabled &&
            this.vtoFoundationEnabled &&
            vtoFoundation?.isDesktopOverlayEnabled
        );
    }

    @computed
    public get shadeSidebarVtoEnabled() {
        const vtoFeature = this.vtoFeature;
        const vtoMakeup = vtoFeature?.makeup;
        const vtoFoundation = vtoFeature?.foundation;
        const hasMakeupShadeSidebarVto =
            this.vtoMakeupFeatureEnabled && vtoMakeup?.hasShadeSidebarVto;
        const hasFoundationShadeSidebarVto =
            this.vtoFoundationFeatureEnabled && vtoFoundation?.hasShadeSidebarVto;

        return !!(hasMakeupShadeSidebarVto || hasFoundationShadeSidebarVto);
    }

    @computed
    public get vtoFeature() {
        return this.config && this.config.vtoFeature;
    }

    @computed
    public get engravingEnabled() {
        const engraving = this.config && this.config.engraving;

        return engraving && engraving.enabled;
    }

    @computed
    public get favoritesEnabled() {
        const features = this.config && this.config.features;

        return features && features.favorites && features.favorites.enabled;
    }

    @computed
    get smartGiftEnabled() {
        const features = this.config && this.config.features;
        const isSmartGiftEnabled = features && features.smartGift && features.smartGift.enabled;

        return isSmartGiftEnabled && this.selectedSku.isGiftable && this.selectedSku.isShoppable;
    }

    @computed
    public get charLimit() {
        return (
            this.config &&
            this.config.productDescription &&
            this.config.productDescription.charLimit
        );
    }

    @computed
    public get shouldRenderReadMoreLink() {
        const { description } = this.product;
        const charLimit = this.charLimit;

        if (!charLimit) {
            return false;
        }

        const descriptionCharCount = description.replace(/ /g, '').length;

        return descriptionCharCount >= charLimit;
    }

    @computed
    public get productTruncatedDescription() {
        const { description } = this.product;
        const charLimit = this.charLimit;

        return charLimit ? truncateStringToNearestWord(description, charLimit) : description;
    }

    @computed
    public get stickyAddToBagVisibleClassName() {
        return this.showStickyAddToBag
            ? STICKY_CLASS_NAMES.SHOW_STICKY
            : STICKY_CLASS_NAMES.HIDE_STICKY;
    }

    @action
    public productUpdateQuantity = (quantity: number) => {
        this.product.setQuantity(quantity);
    };

    @action
    public setShowStickyAddToBag = (showStickyAddToBag: boolean) => {
        this.showStickyAddToBag = showStickyAddToBag;
    };

    @action
    public togglePerfectShades = (isEnabled: boolean) => {
        this.isPerfectShadeEnabled = isEnabled;
    };

    @action
    public toggleProductInfoPopup = () => {
        this.isProductInfoPopupOpen = !this.isProductInfoPopupOpen;
    };

    @action
    public setProductInfoPopupConfig = (payload: IProductInfoPopupPayload) => {
        this.productInfoPopupFieldType = payload.productInfoPopupField;
        this.productInfoPopupHeader = payload.productInfoPopupHeader;
        this.toggleProductInfoPopup();
    };

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;
    };

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @computed
    public get productDetails(): IProductDetails {
        const {
            whoItsFor: { content: whoItsFor },
            greatToKnow: { content: greatToKnow },
            whatItIs: { content: whatItIs },
            whatItDoes: { content: whatItDoes },
            keyIngredient,
            howDoesItWork: { content: howDoesItWork },
            howToUse: { content: howToUse },
            fragranceKeyNotes
        } = this.product;
        const allIngredients = buildIngredientsContent(this.selectedSku);

        return {
            whoItsFor,
            greatToKnow,
            whatItIs,
            whatItDoes,
            keyIngredient,
            allIngredients,
            howDoesItWork,
            howToUse,
            fragranceKeyNotes
        };
    }

    @computed
    private get productInfoPopupData() {
        const details = this.productDetails;
        if (this.config && this.config.productInfoPopupConfig) {
            const { productInfoPopupField } = this.config.productInfoPopupConfig;
            if (productInfoPopupField) {
                return details[<ProductInfoPopupFieldType>productInfoPopupField];
            }
        }
        if (this.productInfoPopupFieldType) {
            return details[<ProductInfoPopupFieldType>this.productInfoPopupFieldType];
        }
    }

    public onShadeSelect = (shadeName: string) => {
        this.handleShadeSelect(shadeName);
        this.urlRouteParam = ProductBaseController.shadeUrlParam;
        this.urlRouteValue = shadeName;
        if (!this.isMobile || !this.isVtoOpened) {
            this.updateUrl(ProductBaseController.shadeUrlParam, shadeName);
        }
    };

    public onSizeSelect = (label: string) => {
        this.handleSizeSelect(label);
        this.urlRouteParam = ProductBaseController.sizeUrlParam;
        this.urlRouteValue = label;
        if (!this.isMobile || !this.isVtoOpened) {
            this.updateUrl(ProductBaseController.sizeUrlParam, label);
        }
    };

    @action
    public setIsVTOOpenedState = (isOpened: boolean) => {
        this.isVtoOpened = isOpened;
    };

    @on(events.PRODUCT_INFO_POPUP_CTA_CLICKED)
    public showProductInfoPopup = (payload: IProductInfoPopupPayload) => {
        if (this.isLoaded) {
            this.setProductInfoPopupConfig(payload);
        }
    };

    @on(events.PRODUCT_SHADE_SELECTED)
    public updateProductShade = (payload: { shadeName: string }) => {
        this.handleShadeSelect(payload.shadeName);
        this.urlRouteParam = ProductBaseController.shadeUrlParam;
        this.urlRouteValue = payload.shadeName;
        if (!this.isMobile || !this.isVtoOpened) {
            this.updateUrl(ProductBaseController.shadeUrlParam, payload.shadeName);
        }
    };

    @on(events.PRODUCT_IMAGE_ENABLED)
    public enableProductImage = (isEnabled: boolean) => {
        this.setIsProductImageEnabled(isEnabled);
    };

    @on(events.VTO_CLOSED)
    public closeVTO = () => {
        this.setIsVTOOpenedState(false);
        this.updateUrl(this.urlRouteParam, this.urlRouteValue);
    };

    @on(events.VTO_OPENED)
    public openVTO = () => {
        this.setIsVTOOpenedState(true);
    };
}
